import { ACTORS, IDS, KESTREL, LEVELS, ONTOLOGY, P, SKU, TEAMS, propertyById } from './kestrelOntology';
import {
    CELL_IDS, COMPOSITION, PACK_SERIAL, PALETTE, PENDING_KEYS,
    ancestorPassports, cellRows, packPassport, publicPayload,
} from './kestrelPassport';
import {
    CELLS_PER_PACK, IMPORT_MAPPING, IMPORT_PACKS, LAB_GROUPS, LAB_META, LAB_QUERY,
    LAB_ROWS, LAB_ROWS_QUERY, REQUESTED_KEYS, importDetail, supplierRequest,
} from './kestrelWorkflows';
import { createClock } from './time';

/*
 * The Kestrel Energy Systems dataset every product vignette draws from: one
 * coherent story about an EV pack model, EVP-750, and one of its pack units.
 *
 *   Model EVP-750 (model granularity)
 *   └ Batch GOT-L2-2609-A (batch)
 *     └ Pack unit EVP750-2609-0147 (item)   <- the hero passport
 *       └ 12 cells shown (of 768)
 *
 * Its recycled content came in through a supplier data request, its packs and
 * cells through a spreadsheet import, and Lab queries across them. The pieces
 * live in kestrelOntology.js (structure and ids), kestrelPassport.js
 * (passports and the public page) and kestrelWorkflows.js (request, import,
 * Lab); import them from here.
 *
 * Anything that shows a time is built by buildKestrel() from one clock, so
 * "just now" is true on the day it is looked at. The useKestrel() hook builds
 * it in the page's `appTimezone`.
 */

export {
    ACTORS, CELL_IDS, CELLS_PER_PACK, COMPOSITION, IDS, IMPORT_MAPPING, IMPORT_PACKS, KESTREL,
    LAB_GROUPS, LAB_META, LAB_QUERY, LAB_ROWS, LAB_ROWS_QUERY, LEVELS, ONTOLOGY, P, PACK_SERIAL, PALETTE,
    PENDING_KEYS, REQUESTED_KEYS, SKU, TEAMS, propertyById,
};
export { ancestorPassports, cellRows, importDetail, packPassport, publicPayload, supplierRequest };

/**
 * The whole dataset, measured from one clock. `timeZone` is the page's
 * `appTimezone` (the zone the app's naive timestamps are read in); `now`
 * defaults to the present.
 *
 * Returns plain data (`model` and `batch` are the pack's ancestor passports)
 * plus stage builders bound to the clock, each taking the
 * point in its vignette's story and defaulting to the end of it:
 *
 * - `pack.stage({ resolved, released })`: the pack passport part-way through
 *   the hero (values arriving, then the release).
 * - `cells.stage({ released })`: the child rows as the release ripples down.
 * - `supplier.stage({ accepted, applied })`: the data request under review.
 * - `imports.stage({ progress })`: the import run, 0 to 1.
 */
export function buildKestrel({ timeZone, now } = {}) {
    const clock = createClock({ timeZone, now });

    return {
        clock,
        brand: KESTREL,
        ontology: ONTOLOGY,
        sku: SKU,
        properties: P,
        ids: IDS,
        levels: LEVELS,
        pack: {
            currentNode: LEVELS.pack,
            discoveryChain: [LEVELS.model, LEVELS.batch],
            // Where its QR code points: the public viewer's `/{snowflake}`
            // (route outside.passport.show). Never requested by a vignette.
            publicUrl: `https://view.synexcloud.com/${IDS.pack}`,
            pending: PENDING_KEYS.map((key) => P[key]),
            passport: packPassport(clock),
            draft: packPassport(clock, { resolved: 0, released: false }),
            stage: (options) => packPassport(clock, options),
        },
        ...ancestorPassports(clock),
        cells: {
            currentNode: LEVELS.cell,
            levelName: ONTOLOGY.data[LEVELS.cell].name,
            discoveryChain: [LEVELS.model, LEVELS.batch, LEVELS.pack],
            total: CELLS_PER_PACK,
            rows: cellRows(clock),
            stage: (options) => cellRows(clock, options),
        },
        public: publicPayload(clock),
        supplier: {
            request: supplierRequest(clock),
            applied: supplierRequest(clock, { applied: true }),
            stage: (options) => supplierRequest(clock, options),
        },
        imports: {
            mapping: IMPORT_MAPPING,
            detail: importDetail(clock),
            stage: (options) => importDetail(clock, options),
        },
        lab: {
            meta: LAB_META,
            query: LAB_QUERY,
            groups: LAB_GROUPS,
            rowsQuery: LAB_ROWS_QUERY,
            rows: LAB_ROWS,
        },
    };
}
